import React, { useState, useEffect, useCallback } from 'react';
import axios from "../../utils/axios";
import { getToken, getUser } from '../../utils/Common';
import Layout from '../../components/Layout';
import * as XLSX from 'xlsx-js-style';
import Moment from 'moment';
import { Download } from 'lucide-react';

function Statistics() {
  const token = React.useMemo(() => getToken(), []);
  const headers = React.useMemo(() => ({ 
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  }), [token]);

  // Session
  const userData = React.useMemo(() => getUser() || {}, []);
  const hcode = userData.hotel_id;
  
  // Base URL for images (using same as axios baseURL without /api)
  const API_BASE_URL = axios.defaults.baseURL?.replace('/api', '') || 'http://localhost:8000';

  // States
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Item statistics states
  const [hotelItems, setHotelItems] = useState([]);
  const [itemSummary, setItemSummary] = useState(null);
  const [loadingItems, setLoadingItems] = useState(true);
  
  // Department statistics states
  const [hotelDepartments, setHotelDepartments] = useState([]);
  const [deptSummary, setDeptSummary] = useState(null);
  const [loadingDepartments, setLoadingDepartments] = useState(true);

  // Fetch top suppliers data
  const fetchTopSuppliers = useCallback(() => {
    if (!hcode || !headers) {
      return;
    }

    setLoading(true);
    axios
      .post('/statistics/top/suppliers', { hcode, year: selectedYear }, { headers })
      .then(function (response) {
        if (response.data && response.data.status === 200) {
          setTopSuppliers(response.data.suppliers || []);
          setChartData(response.data.chart_data || null);
          setSummary(response.data.summary || null);
        }
      })
      .catch(function (error) {


        setTopSuppliers([]);
        setChartData(null);
        setSummary(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hcode, selectedYear]);

  // Fetch top items data
  const fetchTopItems = useCallback(() => {
    if (!hcode || !headers) {
      return;
    }

    setLoadingItems(true);
    axios
      .post('/statistics/top/items', { hcode, year: selectedYear }, { headers })
      .then(function (response) {
        if (response.data && response.data.status === 200) {
          setHotelItems(response.data.hotel_items || []);
          setItemSummary(response.data.summary || null);
          if (response.data.hotel_items?.length === 0) {
          }
        }
      })
      .catch(function (error) {



        if (error.response) {


        }
        setHotelItems([]);
        setItemSummary(null);
      })
      .finally(() => {
        setLoadingItems(false);
      });
  }, [hcode, selectedYear]);

  // Fetch departments data
  const fetchDepartments = useCallback(() => {
    if (!hcode || !headers) {
      return;
    }

    setLoadingDepartments(true);
    axios
      .post('/statistics/top/departments', { hcode, year: selectedYear }, { headers })
      .then(function (response) {
        if (response.data && response.data.status === 200) {
          setHotelDepartments(response.data.hotel_departments || []);
          setDeptSummary(response.data.summary || null);
        }
      })
      .catch(function (error) {

        setHotelDepartments([]);
        setDeptSummary(null);
      })
      .finally(() => {
        setLoadingDepartments(false);
      });
  }, [hcode, selectedYear]);

  // Initial load
  useEffect(() => {
    if (token && hcode) {
      fetchTopSuppliers();
      fetchTopItems();
      fetchDepartments();
    }
  }, [token, hcode, selectedYear]);

  // Generate year options (last 5 years)
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 5; i++) {
    yearOptions.push(currentYear - i);
  }

  // Function to export hotel items to Excel
  const exportHotelItems = (hotelName, hotelCode, items) => {
    // Calculate total
    const total = items.reduce((sum, item) => sum + (parseInt(item.total_value) || 0), 0);

    // Create title
    const title = `Top Items Statistics - ${hotelCode || hotelName}`;
    const subtitle = `Hotel: ${hotelName} | Year: ${selectedYear}`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Rank', 'Item Name', 'Total Orders', 'Total Quantity', 'Unit', 'Avg Price (IDR)', 'Total Value (IDR)'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows
    const dataRows = items.map((item, index) => [
      index + 1,
      item.item_name || '',
      item.total_orders || 0,
      item.total_quantity || 0,
      item.unit || '',
      item.avg_price ? parseInt(item.avg_price).toLocaleString('id-ID') : '0',
      item.total_value ? parseInt(item.total_value).toLocaleString('id-ID') : '0'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    const totalRowData = ['', '', '', '', '', 'TOTAL', total.toLocaleString('id-ID')];
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // Rank
      { wch: 35 }, // Item Name
      { wch: 15 }, // Total Orders
      { wch: 15 }, // Total Quantity
      { wch: 10 }, // Unit
      { wch: 18 }, // Avg Price
      { wch: 20 }  // Total Value
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    for (let R = 6; R <= totalRow - 1; R++) {
      for (let C = 0; C <= 6; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }
        
        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = { 
          vertical: 'center', 
          horizontal: C >= 2 ? 'right' : 'left',
          wrapText: true 
        };

        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Top Items');
    const fileName = `Top_Items_${hotelCode || 'Hotel'}_${selectedYear}_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Function to export hotel departments to Excel
  const exportHotelDepartments = (hotelName, hotelCode, departments) => {
    // Calculate total
    const total = departments.reduce((sum, dept) => sum + (parseInt(dept.total_spending) || 0), 0);

    // Create title
    const title = `Department Statistics - ${hotelCode || hotelName}`;
    const subtitle = `Hotel: ${hotelName} | Year: ${selectedYear}`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[subtitle]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A5' });

    // Table headers (row 7)
    const headers = ['Rank', 'Department', 'Order Count', 'Proposal Count', 'Total Spending (IDR)'];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: 'A7' });

    // Data rows
    const dataRows = departments.map((dept, index) => [
      index + 1,
      dept.department_name || '',
      dept.order_count || 0,
      dept.proposal_count || 0,
      dept.total_spending ? parseInt(dept.total_spending).toLocaleString('id-ID') : '0'
    ]);

    XLSX.utils.sheet_add_aoa(ws, dataRows, { origin: 'A8' });

    // Add total row
    const totalRow = dataRows.length + 8;
    const totalRowData = ['', '', '', 'TOTAL', total.toLocaleString('id-ID')];
    XLSX.utils.sheet_add_aoa(ws, [totalRowData], { origin: `A${totalRow}` });

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // Rank
      { wch: 30 }, // Department
      { wch: 15 }, // Order Count
      { wch: 15 }, // Proposal Count
      { wch: 20 }  // Total Spending
    ];

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    for (let R = 6; R <= totalRow - 1; R++) {
      for (let C = 0; C <= 4; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }
        
        ws[cellAddress].s.border = borderStyle;
        ws[cellAddress].s.alignment = { 
          vertical: 'center', 
          horizontal: C >= 2 ? 'right' : 'left',
          wrapText: true 
        };

        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        if (R === totalRow - 1) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'Departments');
    const fileName = `Departments_${hotelCode || 'Hotel'}_${selectedYear}_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Layout title="Statistics" subtitle="Chart & Statistic Year for All Hotel ">
      <div className="p-4 sm:p-6 md:p-8">
        
        {/* Header */}
        <div className="rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Statistic</h1>
              <p className="text-sm sm:text-base text-gray-600">View and analyze your procurement data</p>
            </div>
            
            {/* Year Selection */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-600">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              
              <button
                onClick={() => {
                  fetchTopSuppliers();
                  fetchTopItems();
                  fetchDepartments();
                }}
                disabled={loading || loadingItems || loadingDepartments}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className={`w-4 h-4 ${(loading || loadingItems || loadingDepartments) ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh All</span>
              </button>
            </div>
          </div>
        </div>

        {/* Suppliers Chart */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl p-8 mb-6 border border-gray-200">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">Top 10 Suppliers by Spending</h2>
            <p className="text-sm text-gray-500 mt-1">Based on received orders in {selectedYear}</p>
          </div>
          
          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-4">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-xs text-blue-800 flex-1">
                <p className="font-semibold mb-1 text-xs sm:text-sm">How to read:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                  <div>🥇🥈🥉 Top 3 suppliers</div>
                  <div><strong>Amount</strong> = Total paid</div>
                  <div className="sm:col-span-2"><strong>%</strong> = Share of spending | <strong>Data</strong> = Confirmed orders only</div>
                </div>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : topSuppliers.length > 0 ? (
            <div className="bg-white rounded-xl p-6 shadow-inner">
              <svg className="w-full" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid meet">
                {/* Background with gradient */}
                <defs>
                  <linearGradient id="bgGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#fafafa" />
                    <stop offset="100%" stopColor="#ffffff" />
                  </linearGradient>
                </defs>
                <rect width="1200" height="700" fill="url(#bgGradient)" />
                
                {/* Chart Area with subtle border */}
                <rect x="250" y="50" width="900" height="630" fill="white" stroke="#e5e7eb" strokeWidth="2" rx="8" />
                
                {/* Grid lines */}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <line
                    key={i}
                    x1="250"
                    y1={70 + (i * 60)}
                    x2="1150"
                    y2={70 + (i * 60)}
                    stroke="#f3f4f6"
                    strokeWidth="1"
                    strokeDasharray="5,5"
                  />
                ))}
                
                {/* Bars with enhanced styling */}
                {topSuppliers.map((supplier, index) => {
                  const maxSpending = Math.max(...topSuppliers.map(s => s.total_spent));
                  const barHeight = 45;
                  const barWidth = (supplier.total_spent / maxSpending) * 850;
                  const yPos = 78 + (index * 60);
                  const color = chartData?.datasets[0]?.backgroundColor[index] || 'rgba(147, 51, 234, 0.8)';
                  
                  return (
                    <g key={index}>
                      {/* Bar shadow */}
                      <rect
                        x="272"
                        y={yPos + 3}
                        width={barWidth}
                        height={barHeight}
                        fill="rgba(0,0,0,0.1)"
                        rx="6"
                      />
                      
                      {/* Bar */}
                      <rect
                        x="270"
                        y={yPos}
                        width={barWidth}
                        height={barHeight}
                        fill={color}
                        rx="6"
                        className="hover:opacity-80 transition-opacity"
                      />
                      
                      {/* Rank badge */}
                      <circle
                        cx="230"
                        cy={yPos + barHeight/2}
                        r="16"
                        fill={index < 3 ? '#fbbf24' : '#d1d5db'}
                      />
                      <text
                        x="230"
                        y={yPos + barHeight/2 + 5}
                        fill="white"
                        fontSize="12"
                        fontWeight="700"
                        textAnchor="middle"
                      >
                        {index + 1}
                      </text>
                      
                      {/* Value inside bar if it fits, otherwise outside */}
                      <text
                        x={barWidth > 200 ? 280 : 280 + barWidth + 10}
                        y={yPos + barHeight/2 + 5}
                        fill={barWidth > 200 ? "white" : "#1f2937"}
                        fontSize="13"
                        fontWeight="700"
                      >
                        IDR {supplier.total_spent.toLocaleString('id-ID')}
                      </text>
                      
                      {/* Supplier Name on left */}
                      <text
                        x="20"
                        y={yPos + barHeight/2 + 5}
                        fill="#374151"
                        fontSize="12"
                        fontWeight="600"
                        textAnchor="start"
                      >
                        {supplier.company_name.length > 28 
                          ? supplier.company_name.substring(0, 28) + '...' 
                          : supplier.company_name}
                      </text>
                      
                      {/* Percentage */}
                      <text
                        x="1170"
                        y={yPos + barHeight/2 + 5}
                        fill="#6b7280"
                        fontSize="11"
                        fontWeight="600"
                        textAnchor="end"
                      >
                        {supplier.percentage}%
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500 text-lg mb-2">No Data Available</p>
              <p className="text-gray-400 text-sm">No supplier data found for {selectedYear}</p>
            </div>
          )}
        </div>

        {/* Supplier Table */}
        {topSuppliers.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Detailed Supplier List</h2>
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden">
              <div className="p-4 space-y-4">
                {topSuppliers.map((supplier, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-200 text-gray-800' :
                        index === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        #{index + 1}
                      </span>
                      <span className="text-xs font-semibold text-purple-600">{supplier.percentage}%</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-3">{supplier.company_name}</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Total Spent</p>
                        <p className=" text-gray-900">IDR {supplier.total_spent.toLocaleString('id-ID')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Orders</p>
                        <p className="font-semibold text-gray-900">{supplier.total_orders}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Avg Order</p>
                        <p className="font-semibold text-gray-900">IDR {supplier.avg_order_value.toLocaleString('id-ID')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Items</p>
                        <p className="font-semibold text-gray-900">{supplier.total_items_received}</p>
                      </div>
                    </div>
                    <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{width: `${Math.min(supplier.percentage, 100)}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Share %</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {topSuppliers.map((supplier, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-yellow-100 text-yellow-800' :
                          index === 1 ? 'bg-gray-200 text-gray-800' :
                          index === 2 ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          #{index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{supplier.company_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          IDR {supplier.total_spent.toLocaleString('id-ID')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{supplier.total_orders}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          IDR {supplier.avg_order_value.toLocaleString('id-ID')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{supplier.total_items_received}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900 mr-2">{supplier.percentage}%</div>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-purple-600 h-2 rounded-full" 
                              style={{width: `${Math.min(supplier.percentage, 100)}%`}}
                            ></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top 5 Items Per Hotel */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border mt-2 border-gray-200">
          <div className="mb-4 flex items-center space-x-3">
            <h2 className="text-lg font-bold text-gray-800">Top 5 Items Per Hotel</h2>
            <p className="text-sm text-gray-500">Most frequently ordered items by each hotel in {selectedYear}</p>
          </div>
          
          {/* Info Note */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-4">
            <div className="flex items-center gap-4 text-xs text-green-800 whitespace-nowrap overflow-x-auto">
              <div className="font-semibold">How to read:</div>
              <div><strong>Card</strong> = One hotel's top 5</div>
              <div><strong>Count</strong> = Order frequency</div>
              <div><strong>Data</strong> = Confirmed received orders only</div>
            </div>
          </div>
          
          {loadingItems ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : hotelItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-6">
              {hotelItems.map((hotel, hotelIndex) => (
                <div key={hotelIndex} className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100 hover:border-blue-300 transition-colors">
                  <div className="mb-4 pb-3 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{hotel.hotel_name}</h3>
                      </div>
                      <button
                        onClick={() => exportHotelItems(hotel.hotel_name, hotel.hotel_code, hotel.items)}
                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        title={`Download Excel - ${hotel.hotel_code}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {hotel.items && hotel.items.length > 0 ? (
                      hotel.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors">
                          <div className="flex-shrink-0 relative">
                            {item.img_item ? (
                              <img 
                                src={`${API_BASE_URL}/storage/item/${item.img_item}`}
                                alt={item.item_name}
                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg object-cover border-2 border-gray-200"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="%23ccc" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                              </div>
                            )}
                            <span className={`absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              itemIndex === 0 ? 'bg-blue-500 text-white' :
                              itemIndex === 1 ? 'bg-green-500 text-white' :
                              itemIndex === 2 ? 'bg-yellow-500 text-white' :
                              'bg-gray-400 text-white'
                            }`}>
                              {itemIndex + 1}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{item.item_name}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-600">{item.total_quantity} {item.unit}</span>
                              <span className="text-xs font-bold text-blue-600">{item.order_count}x orders</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">IDR {item.total_value.toLocaleString('id-ID')}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-400 italic text-center py-4">No items data</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p className="text-gray-500 text-lg mb-2">No Data Available</p>
              <p className="text-gray-400 text-sm">No hotel items found for {selectedYear}</p>
            </div>
          )}
        </div>

        {/* Top 10 Departments Per Hotel */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mt-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800">Top 10 Departments Per Hotel</h2>
            <p className="text-sm text-gray-500">Most active departments by order frequency in {selectedYear}</p>
          </div>
          
          {/* Info Note */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 mb-4">
            <div className="flex items-center gap-4 text-xs text-purple-800 whitespace-nowrap overflow-x-auto">
              <div className="font-semibold">How to read:</div>
              <div><strong>Section</strong> = Hotel's top 10 depts</div>
              <div><strong>Bar</strong> = Relative activity</div>
              <div><strong>Count</strong> = Order frequency</div>
              <div><strong>Data</strong> = Confirmed orders</div>
            </div>
          </div>
          
          {loadingDepartments ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : hotelDepartments.length > 0 ? (
            <div className="space-y-8">
              {hotelDepartments.map((hotel, hotelIndex) => (
                <div key={hotelIndex} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <div className="mb-3 pb-2 border-b-2 border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-base font-bold text-gray-900">{hotel.hotel_name}</h3>
                      </div>
                      <button
                        onClick={() => exportHotelDepartments(hotel.hotel_name, hotel.hotel_code, hotel.departments)}
                        className="p-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        title={`Download Excel - ${hotel.hotel_code}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {hotel.departments && hotel.departments.length > 0 ? (
                    <div className="space-y-2">
                      {hotel.departments.map((dept, deptIndex) => {
                        const maxOrders = Math.max(...hotel.departments.map(d => d.order_count));
                        const barWidth = (dept.order_count / maxOrders) * 100;
                        
                        return (
                          <div key={deptIndex} className="group">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-1.5 gap-1.5">
                              <div className="flex items-center space-x-1.5 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  deptIndex === 0 ? 'bg-purple-500 text-white' :
                                  deptIndex === 1 ? 'bg-blue-500 text-white' :
                                  deptIndex === 2 ? 'bg-green-500 text-white' :
                                  'bg-gray-300 text-gray-700'
                                }`}>
                                  #{deptIndex + 1}
                                </span>
                                <span className="font-semibold text-gray-900 text-xs">{dept.department_name}</span>
                                <span className="text-[10px] text-gray-500">({dept.department_code})</span>
                              </div>
                              <div className="text-left sm:text-right">
                                <span className="text-[10px] font-bold text-purple-600">{dept.order_count}x orders</span>
                              </div>
                            </div>
                            <div className="relative h-6 bg-gray-100 rounded-lg overflow-hidden">
                              <div 
                                className={`absolute left-0 top-0 h-full rounded-lg transition-all duration-500 ${
                                  deptIndex === 0 ? 'bg-gradient-to-r from-purple-500 to-purple-600' :
                                  deptIndex === 1 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                  deptIndex === 2 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                  'bg-gradient-to-r from-gray-400 to-gray-500'
                                }`}
                                style={{ width: `${barWidth}%` }}
                              >
                                <div className="flex items-center justify-between h-full px-2">
                                  <span className="text-[10px] text-white font-semibold truncate">
                                    {dept.proposal_count} proposals
                                  </span>
                                  {barWidth > 40 && (
                                    <span className="text-[10px] text-white hidden sm:inline">
                                      IDR {dept.total_spending.toLocaleString('id-ID')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {barWidth <= 40 && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600 truncate">
                                  IDR {(dept.total_spending / 1000000).toFixed(1)}M
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic text-center py-3">No department data</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-gray-500 text-lg mb-2">No Data Available</p>
              <p className="text-gray-400 text-sm">No department data found for {selectedYear}</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Statistics;
